import { z } from 'zod';

/** The eight first-class roles. RBAC = role -> set of permission strings (see PERMISSIONS). */
export const ROLES = [
  'SUPER_ADMIN',
  'ADMIN_STAFF',
  'VENDOR',
  'CUSTOMER',
  'REFERRAL_AGENT',
  'TEAM_LEADER',
  'DELIVERY_AGENT',
  'POS_STAFF',
] as const;
export const RoleSchema = z.enum(ROLES);
export type Role = z.infer<typeof RoleSchema>;

/** Admin-staff department presets — these expand to permission bundles, they are not roles. */
export const ADMIN_DEPARTMENTS = [
  'OPERATIONS',
  'FINANCE',
  'CRM',
  'WAREHOUSE',
  'COMPLIANCE',
  'CATALOG',
] as const;
export const AdminDepartmentSchema = z.enum(ADMIN_DEPARTMENTS);
export type AdminDepartment = z.infer<typeof AdminDepartmentSchema>;

/** Granular permission strings checked by the RBAC middleware. Extend as modules land. */
export const PERMISSIONS = [
  // platform
  'platform.settings.read',
  'platform.settings.write',
  'platform.feature_flags.write',
  'audit.read',
  // users / vendors
  'user.read',
  'user.write',
  'user.role.write',
  'vendor.read',
  'vendor.approve',
  'vendor.suspend',
  // catalog
  'category.write',
  'product.read.all',
  'product.approve',
  'product.write.any',
  'brand.read',
  'brand.write',
  'attribute.read',
  'attribute.write',
  'color.read',
  'color.write',
  'catalog.discount.read',
  'catalog.discount.write',
  'catalog.import.read',
  'catalog.import.write',
  'note.read',
  'note.write',
  'customer.read',
  'customer.write',
  'classified.read',
  'classified.write',
  'agent.read',
  'agent.write',
  'file.read',
  'file.write',
  // commerce
  'order.read.all',
  'order.refund',
  'shipping.write',
  'commission.read',
  'commission.write',
  'tax.config.write',
  // money
  'payout.read',
  'payout.release',
  // wallet & ledger
  'wallet.read.self',
  'wallet.read.any',
  'wallet.credit.issue',
  'wallet.credit.revoke',
  'wallet.dispute.resolve',
  'wallet.reserve.write',
  'ledger.read',
  'ledger.adjust',
  // payments
  'payment.read.self',
  'payment.read.any',
  'payment.refund',
  // settlements & vendor finance (note: `payout.release` defined above under // money)
  'settlement.read.self',
  'settlement.read.any',
  'settlement.run',
  'settlement.adjust',
  'payout.request.self',
  'payout.approve',
  'vendor.bank.write.self',
  'vendor.bank.verify',
  // engagement
  'review.moderate',
  'cms.write',
  /** Payment, courier, SMS, email and storage credentials. Super admins only by default. */
  'settings.integrations.manage',
  'marketing.read',
  'marketing.write',
  // CRM / lifecycle
  'crm.event.read',
  'crm.event.write',
  'crm.metrics.read',
  'crm.automation.run',
  // notifications
  'notification.template.write',
  'notification.send',
  'notification.log.read',
  // support
  'support.ticket.read.self',
  'support.ticket.read.any',
  'support.ticket.read.vendor',
  'support.ticket.reply.vendor',
  'support.ticket.resolve.vendor',
  'support.ticket.attachment.upload',
  'support.ticket.escalate',
  'support.ticket.assign',
  'support.ticket.update',
  'support.ticket.metrics.read',
  // ERP — procurement, internal inventory, AP, expenses
  'erp.supplier.read',
  'erp.supplier.write',
  'erp.warehouse.write',
  'erp.po.read',
  'erp.po.write',
  'erp.po.approve',
  'erp.grn.write',
  'erp.bill.read',
  'erp.bill.write',
  'erp.bill.approve',
  'erp.bill.pay',
  'erp.expense.submit',
  'erp.expense.approve',
  'erp.expense.pay',
  'erp.inventory.read',
  'erp.inventory.transfer',
  'erp.inventory.damage',
  // POS — point of sale (Layer 5)
  'pos.branch.write',
  'pos.terminal.write',
  'pos.shift.open',
  'pos.shift.close',
  'pos.shift.read.self',
  'pos.shift.read.any',
  'pos.sale.create',
  'pos.sale.refund',
  'pos.sale.read.self',
  'pos.sale.read.any',
  'pos.report.read',
  // B2B Wholesale (Layer 6)
  'b2b.account.read.self',
  'b2b.account.read.any',
  'b2b.account.write',
  'b2b.salesrep.write',
  'b2b.contract.write',
  'b2b.pricelist.write',
  'b2b.rfq.read.self',
  'b2b.rfq.read.any',
  'b2b.rfq.write.self',
  'b2b.rfq.quote',
  'b2b.order.read.self',
  'b2b.order.read.any',
  'b2b.order.write.self',
  'b2b.order.approve',
  'b2b.order.fulfill',
  'b2b.invoice.read.self',
  'b2b.invoice.read.any',
  'b2b.invoice.issue',
  'b2b.payment.record',
  // SAAS — vendor subscriptions + sponsored listings (Layer 7)
  'saas.plan.write',
  'saas.plan.read',
  'saas.subscription.read.self',
  'saas.subscription.read.any',
  'saas.subscription.manage.self',
  'saas.subscription.manage.any',
  'saas.invoice.read.self',
  'saas.invoice.read.any',
  'saas.invoice.record',
  'saas.sponsored.write.self',
  'saas.sponsored.read.self',
  'saas.sponsored.read.any',
  'saas.sponsored.event.record',
  // Analytics (Layer 8)
  'analytics.read.platform',
  'analytics.read.vendor.self',
  'analytics.read.customer.self',
  'analytics.run',
  // Intelligence — Unified Data Layer + Signals (Phase 3.1)
  'intelligence.read.platform',
  'intelligence.read.vendor.self',
  'intelligence.read.customer.self',
  'intelligence.etl.run',
  'intelligence.fraud.flag',
  'intelligence.fraud.resolve',
  'intelligence.pricing.propose',
  'intelligence.pricing.resolve',
  'intelligence.interaction.record',
  // AI Commerce Layer (Phase 3.2)
  'ai.recommendations.read',
  'ai.recommendations.read.self',
  'ai.pricing.scan',
  'ai.fraud.scan',
  'ai.content.tag',
  'ai.jobs.read',
  'ai.jobs.manage',
  // Network Engine — referrals, teams, ranks, MLM (Phase 3.3)
  'network.referral.read.self',
  'network.referral.read.any',
  'network.referral.campaign.write',
  'network.referral.attach',
  'network.referral.click.record',
  'network.referral.convert',
  'network.team.read.self',
  'network.team.read.any',
  'network.team.write',
  'network.team.lead',
  'network.rank.read.self',
  'network.rank.read.any',
  'network.rank.engine.run',
  'network.rank.tier.write',
  'network.mlm.rule.write',
  'network.mlm.engine.run',
  'network.reward.release',
  // Mobile Ecosystem (Phase 3.4)
  'mobile.device.register.self',
  'mobile.device.read.self',
  'mobile.device.read.any',
  'mobile.app.version.write',
  'mobile.app.version.read',
  'mobile.push.send',
  // Platform / Service Registry (Phase 3.5)
  'platform.service.read',
  'platform.service.write',
  'platform.service.heartbeat',
  'platform.event.publish',
  'platform.event.read',
  'platform.metrics.read',
  // Scale Infrastructure (Phase 3.6)
  'scale.apikey.read',
  'scale.apikey.issue',
  'scale.apikey.revoke',
  'scale.security.read',
  'scale.security.record',
  'scale.flag.read',
  'scale.flag.write',
  'scale.flag.evaluate',
  'scale.upstream.read',
  'scale.upstream.probe',
  // Globalization (Phase 3.7)
  'globalization.country.read',
  'globalization.country.write',
  'globalization.currency.read',
  'globalization.currency.write',
  'globalization.fx.record',
  'globalization.fx.convert',
  'globalization.locale.write',
  'globalization.translation.read',
  'globalization.translation.write',
  'globalization.tax.read',
  'globalization.tax.write',
  'globalization.tax.quote',
] as const;
export const PermissionSchema = z.enum(PERMISSIONS);
export type Permission = z.infer<typeof PermissionSchema>;

export const AccountStatusSchema = z.enum([
  'ACTIVE',
  'PENDING_VERIFICATION',
  'SUSPENDED',
  'DISABLED',
]);
export type AccountStatus = z.infer<typeof AccountStatusSchema>;

export const KycStatusSchema = z.enum([
  'NOT_SUBMITTED',
  'SUBMITTED',
  'UNDER_REVIEW',
  'APPROVED',
  'REJECTED',
]);
export type KycStatus = z.infer<typeof KycStatusSchema>;

/**
 * Vendor lifecycle.
 * Canonical: DRAFT → PENDING_APPROVAL → (APPROVED | REJECTED | CHANGES_REQUESTED).
 * `PENDING` and `UNDER_REVIEW` are DEPRECATED legacy enum values (kept for
 * backward compatibility with older databases). Producers MUST NOT emit them;
 * use `PENDING_APPROVAL` instead.
 */
export const VendorStatusSchema = z.enum([
  'DRAFT',
  'PENDING_APPROVAL',
  'CHANGES_REQUESTED',
  'APPROVED',
  'REJECTED',
  'SUSPENDED',
  'PENDING', // @deprecated use PENDING_APPROVAL
  'UNDER_REVIEW', // @deprecated use PENDING_APPROVAL
]);
export type VendorStatus = z.infer<typeof VendorStatusSchema>;

export const VendorTierSchema = z.enum(['FREE', 'PRO', 'ENTERPRISE']);
export type VendorTier = z.infer<typeof VendorTierSchema>;

export const ProductStatusSchema = z.enum([
  'DRAFT',
  'PENDING_APPROVAL',
  'CHANGES_REQUESTED',
  'APPROVED',
  'REJECTED',
  'ARCHIVED',
]);
export type ProductStatus = z.infer<typeof ProductStatusSchema>;

export const SubOrderStatusSchema = z.enum([
  'PENDING_PAYMENT',
  'PLACED',
  'CONFIRMED',
  'PACKED',
  'SHIPPED',
  'DELIVERED',
  'CLOSED',
  'CANCELLED',
  'RETURN_REQUESTED',
  'RETURN_APPROVED',
  'RETURN_RECEIVED',
  'REFUNDED',
]);
export type SubOrderStatus = z.infer<typeof SubOrderStatusSchema>;

export const PaymentStatusSchema = z.enum([
  'CREATED',
  'AUTHORIZED',
  'CAPTURED',
  'FAILED',
  'REFUNDED',
  'PARTIALLY_REFUNDED',
  'COD_PENDING',
  'COD_COLLECTED',
]);
export type PaymentStatus = z.infer<typeof PaymentStatusSchema>;

export const PaymentMethodSchema = z.enum([
  'UPI',
  'CARD',
  'NETBANKING',
  'WALLET',
  'EMI',
  'QR',
  'COD',
]);
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;

export const GstRateSchema = z.union([
  z.literal(0),
  z.literal(3),
  z.literal(5),
  z.literal(12),
  z.literal(18),
  z.literal(28),
]);
export type GstRate = z.infer<typeof GstRateSchema>;
